// ─── Model Registry ───────────────────────────────────────────────────────────
//
// Stores the full CollectionSchema (Phase 3) including deferred fields:
//   - indexes    (owned by queryEngine in a future phase)
//   - computedFields (owned by computedQueries.ts in Layer 2B)
//   - timestamps (informational; dataEngine always stamps regardless)
//
// Lookup by name (primary, matches permissionEngine + dataEngine key convention)
// and by id (secondary, for schema-id references).

import type { CollectionSchema } from '@/core/schema/dataSchema';

class ModelRegistry {
  private byName = new Map<string, CollectionSchema>();
  private byId   = new Map<string, CollectionSchema>();
  private currentProjectId: string | null = null;

  registerAll(projectId: string, schemas: CollectionSchema[]): void {
    this.currentProjectId = projectId;
    this.byName.clear();
    this.byId.clear();
    for (const s of schemas) {
      this.byName.set(s.name, s);
      this.byId.set(s.id, s);
    }
  }

  getByName(name: string): CollectionSchema | undefined {
    return this.byName.get(name);
  }

  getById(id: string): CollectionSchema | undefined {
    return this.byId.get(id);
  }

  all(): CollectionSchema[] {
    return [...this.byName.values()];
  }

  allNames(): string[] {
    return [...this.byName.keys()];
  }

  has(name: string): boolean {
    return this.byName.has(name);
  }

  getProjectId(): string | null {
    return this.currentProjectId;
  }

  clear(): void {
    this.byName.clear();
    this.byId.clear();
    this.currentProjectId = null;
  }
}

export const modelRegistry = new ModelRegistry();
